import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";

// =============================================================================
// STANDARD PROPERTY TYPES
// =============================================================================

/** Standard properties that should be included on all events where applicable */
export type StandardEventProperties = {
  /** Organization context */
  organization_id?: string;
  /** Project context */
  project_id?: string;
  /** Human-readable project context */
  project_name?: string;
  /** Who/what triggered the action */
  action_source?: "manual" | "ai";
  /** Result of the action */
  action_outcome?: "success" | "failure" | "cancelled" | "fallback";
  /** Time taken for the action in milliseconds */
  duration_ms?: number;
  /** Human-readable error message on failures */
  error_message?: string;
  /** Number of items affected */
  item_count?: number;
};

// =============================================================================
// EVENT CONSTANTS
// =============================================================================

export const POSTHOG_EVENTS = {
  // -------------------------------------------------------------------------
  // AUTH EVENTS
  // -------------------------------------------------------------------------
  AUTH_LOGIN: "login",
  AUTH_LOGOUT: "logout",
  AUTH_SIGNUP: "signup",

  // -------------------------------------------------------------------------
  // PROJECT EVENTS
  // -------------------------------------------------------------------------
  PROJECT_CREATED: "created",
  PROJECT_DELETED: "deleted",
  PROJECT_CREATION_FAILED: "creation_failed",
  PROJECT_DELETION_FAILED: "deletion_failed",
  PROJECT_CREATION_MODAL_OPENED: "creation_modal_opened",
  PROJECT_CREATION_SOURCE_SELECTED: "creation_source_selected",
  PROJECT_CREATION_FILES_CONFIRMED: "creation_files_confirmed",
  PROJECT_CREATION_CONTEXT_COMPLETED: "creation_context_completed",
  PROJECT_CREATION_MODAL_CLOSED: "creation_modal_closed",

  // -------------------------------------------------------------------------
  // AI DOCUMENT EVENTS
  // -------------------------------------------------------------------------
  // Creation modal events
  AI_DOCUMENT_MODAL_OPENED: "modal_opened",
  AI_DOCUMENT_PROJECT_SELECTED: "project_selected",
  AI_DOCUMENT_TEMPLATE_SELECTED: "template_selected",
  AI_DOCUMENT_SECTIONS_CONFIRMED: "sections_confirmed",
  AI_DOCUMENT_SECTION_ADDED: "section_added",
  AI_DOCUMENT_SECTION_REMOVED: "section_removed",
  AI_DOCUMENT_SECTIONS_REORDERED: "sections_reordered",
  AI_DOCUMENT_ROLE_CHANGED: "role_changed",
  AI_DOCUMENT_FUNDING_CHANGED: "funding_changed",
  AI_DOCUMENT_FOCUS_EDITED: "focus_edited",
  AI_DOCUMENT_GENERATE_CLICKED: "generate_clicked",
  AI_DOCUMENT_GENERATE_TRIGGERED: "generate_triggered",
  AI_DOCUMENT_GENERATE_FAILED: "generate_failed",
  AI_DOCUMENT_MORE_TEMPLATES_CLICKED: "more_templates_clicked",
  AI_DOCUMENT_TEMPLATE_SUGGESTED: "template_suggested",
  AI_DOCUMENT_TEMPLATE_SEARCHED: "template_searched",
  // Lifecycle events
  AI_DOCUMENT_OPENED: "opened",
  AI_DOCUMENT_EXPORTED: "exported",
  AI_DOCUMENT_RESTORED: "restored",
  AI_DOCUMENT_DELETED: "deleted",
  // UI interaction events
  AI_DOCUMENT_CHAT_TOGGLED: "chat_toggled",
  AI_DOCUMENT_CHAT_CONVERSATION_PICKER_OPENED: "chat_conversation_picker_opened",
  AI_DOCUMENT_HISTORY_TOGGLED: "history_toggled",
  AI_DOCUMENT_EXPORT_HISTORY_OPENED: "export_history_opened",
  AI_DOCUMENT_TOC_NAVIGATION_CLICKED: "toc_navigation_clicked",
  // Engagement analytics events
  AI_DOCUMENT_CLOSED: "closed",
  AI_DOCUMENT_EDITED: "edited",
  AI_DOCUMENT_BLOCK_DWELL: "block_dwell",
  AI_DOCUMENT_MODAL_ABANDONED: "modal_abandoned",

  // -------------------------------------------------------------------------
  // CHAT EVENTS
  // -------------------------------------------------------------------------
  CHAT_CONVERSATION_CREATED: "conversation_created",
  CHAT_CONVERSATION_SWITCHED: "conversation_switched",
  CHAT_MESSAGE_SENT: "message_sent",
  CHAT_NEW_CHAT_SELECTED: "new_chat_selected",
  CHAT_SUGGESTION_USED: "suggestion_used",

  // -------------------------------------------------------------------------
  // PROCUREMENT EVENTS
  // -------------------------------------------------------------------------
  PROCUREMENT_PACKAGE_OPENED: "package_opened",
  PROCUREMENT_CREATION_MODAL_OPENED: "creation_modal_opened",
  PROCUREMENT_CREATION_FORM_COMPLETED: "creation_form_completed",
  PROCUREMENT_CREATION_MODAL_CLOSED: "creation_modal_closed",
  PROCUREMENT_PACKAGE_EXPORTED: "package_exported",
  PROCUREMENT_PACKAGE_FILE_VIEWED: "package_file_viewed",
  PROCUREMENT_PACKAGE_GENERATED: "package_generated",
  PROCUREMENT_PACKAGE_FILE_FEEDBACK: "package_file_feedback",
  PROCUREMENT_PACKAGE_FILE_ADDED: "package_file_added",
  PROCUREMENT_PACKAGE_FILE_REMOVED: "package_file_removed",
  PROCUREMENT_PACKAGE_REGENERATE_DRAWING_INDEX: "package_regenerate_drawing_index",
  PROCUREMENT_PACKAGE_REGENERATE_BUNDLE: "package_regenerate_bundle",

  // -------------------------------------------------------------------------
  // SETTINGS EVENTS
  // -------------------------------------------------------------------------
  SETTINGS_PROFILE_UPDATED: "profile_updated",
  SETTINGS_GOAL_CHANGED: "goal_changed",
  SETTINGS_LANGUAGE_CHANGED: "language_changed",
  SETTINGS_INTEGRATION_CONNECTED: "integration_connected",
  SETTINGS_INTEGRATION_DISCONNECTED: "integration_disconnected",
  SETTINGS_INTEGRATION_CONFIGURED: "integration_configured",

  // -------------------------------------------------------------------------
  // NAVIGATION EVENTS
  // -------------------------------------------------------------------------
  MAGIC_INPUT_USED: "magic_input_used",

  // -------------------------------------------------------------------------
  // GENERIC CROSS-MODULE EVENTS
  // -------------------------------------------------------------------------
  /** Generic feedback event - use target_type to specify what received feedback */
  FEEDBACK_SUBMITTED: "feedback_submitted",
  /** Generic text copy event - use source to specify where text was copied from */
  TEXT_COPIED: "text_copied",
  /** Generic feature usage */
  FEATURE_USED: "feature_used",
  /** Error occurred */
  ERROR_OCCURRED: "error_occurred",
  UNCAUGHT_ERROR: "uncaught_error",
  /** Role change */
  ROLE_CHANGED: "role_changed",

  // -------------------------------------------------------------------------
  // LEGACY EVENTS (deprecated)
  // -------------------------------------------------------------------------
  /** @deprecated Use TEXT_COPIED with source property instead */
  COPY_TO_CLIPBOARD: "copy_to_clipboard",
  /** @deprecated Use CHAT_MESSAGE_SENT instead */
  REGENERATE_MESSAGE: "regenerate_message",
  /** @deprecated Use FEEDBACK_SUBMITTED with rating: "positive" instead */
  THUMBS_UP: "thumbs_up",
  /** @deprecated Use FEEDBACK_SUBMITTED with rating: "negative" instead */
  THUMBS_DOWN: "thumbs_down",
} as const;

// =============================================================================
// USER IDENTIFICATION
// =============================================================================

export const usePostHogIdentification = () => {
  const posthog = usePostHog();
  const { user, userMetadata, organization } = useAuth();

  const previousValuesRef = useRef<{
    userId?: string;
    email?: string;
    name?: string;
    selectedRoles?: string;
    customRole?: string;
    organizationId?: string;
    organizationName?: string;
  }>({});

  useEffect(() => {
    try {
      if (user && posthog) {
        const currentValues = {
          userId: user.id,
          email: user.email,
          name: user.user_metadata?.full_name || user.email?.split("@")[0],
          selectedRoles: userMetadata?.selectedRoles?.join(","),
          customRole: userMetadata?.customRole,
          organizationId: organization?.id,
          organizationName: organization?.name,
        };

        const hasChanged = Object.keys(currentValues).some(
          (key) =>
            previousValuesRef.current[key as keyof typeof currentValues] !==
            currentValues[key as keyof typeof currentValues],
        );

        if (hasChanged) {
          posthog.identify(user.id, {
            email: user.email,
            name: currentValues.name,
            selected_roles: currentValues.selectedRoles,
            custom_role: currentValues.customRole,
            organization_id: organization?.id,
            organization_name: organization?.name,
            created_at: user.created_at,
          });

          if (organization?.id && previousValuesRef.current.organizationId !== organization.id) {
            posthog.group("organization", organization.id, {
              name: organization.name,
              created_at: organization.created_at,
            });
          }

          previousValuesRef.current = currentValues;
        }
      } else if (!user && posthog && previousValuesRef.current.userId) {
        posthog.reset();
        previousValuesRef.current = {};
      }
    } catch (error) {
      console.warn("PostHog identification error:", error);
    }
  }, [user, userMetadata, organization, posthog]);
};

// =============================================================================
// TRACKING FUNCTIONS
// =============================================================================

type PostHogInstance = ReturnType<typeof usePostHog>;

// -----------------------------------------------------------------------------
// Auth Events
// -----------------------------------------------------------------------------

export const trackAuthEvent = (
  posthog: PostHogInstance,
  event:
    | typeof POSTHOG_EVENTS.AUTH_LOGIN
    | typeof POSTHOG_EVENTS.AUTH_LOGOUT
    | typeof POSTHOG_EVENTS.AUTH_SIGNUP,
  properties: StandardEventProperties & {
    method?: "email" | "oauth" | "microsoft";
    success: boolean;
  },
) => {
  if (!posthog) return;
  try {
    posthog.capture(`auth_${event}`, properties);
  } catch (error) {
    console.warn("PostHog tracking error:", error);
  }
};

// -----------------------------------------------------------------------------
// Project Events
// -----------------------------------------------------------------------------

type ProjectEvent =
  | typeof POSTHOG_EVENTS.PROJECT_CREATED
  | typeof POSTHOG_EVENTS.PROJECT_DELETED
  | typeof POSTHOG_EVENTS.PROJECT_CREATION_FAILED
  | typeof POSTHOG_EVENTS.PROJECT_DELETION_FAILED
  | typeof POSTHOG_EVENTS.PROJECT_CREATION_MODAL_OPENED
  | typeof POSTHOG_EVENTS.PROJECT_CREATION_SOURCE_SELECTED
  | typeof POSTHOG_EVENTS.PROJECT_CREATION_FILES_CONFIRMED
  | typeof POSTHOG_EVENTS.PROJECT_CREATION_CONTEXT_COMPLETED
  | typeof POSTHOG_EVENTS.PROJECT_CREATION_MODAL_CLOSED;

export const trackProjectEvent = (
  posthog: PostHogInstance,
  event: ProjectEvent | string,
  properties: StandardEventProperties & {
    project_id: string;
    [key: string]: string | number | boolean | undefined;
  },
) => {
  if (!posthog) return;
  try {
    posthog.capture(`project_${event}`, properties);
  } catch (error) {
    console.warn("PostHog tracking error:", error);
  }
};

// -----------------------------------------------------------------------------
// AI Document Events
// -----------------------------------------------------------------------------

type AIDocumentEvent =
  // Creation modal events
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_MODAL_OPENED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_PROJECT_SELECTED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_TEMPLATE_SELECTED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_SECTIONS_CONFIRMED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_SECTION_ADDED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_SECTION_REMOVED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_SECTIONS_REORDERED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_ROLE_CHANGED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_FUNDING_CHANGED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_FOCUS_EDITED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_GENERATE_CLICKED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_GENERATE_TRIGGERED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_GENERATE_FAILED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_MORE_TEMPLATES_CLICKED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_TEMPLATE_SUGGESTED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_TEMPLATE_SEARCHED
  // Lifecycle events
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_OPENED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_EXPORTED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_RESTORED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_DELETED
  // UI interaction events
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_CHAT_TOGGLED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_CHAT_CONVERSATION_PICKER_OPENED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_HISTORY_TOGGLED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_EXPORT_HISTORY_OPENED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_TOC_NAVIGATION_CLICKED
  // Engagement analytics events
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_CLOSED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_EDITED
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_BLOCK_DWELL
  | typeof POSTHOG_EVENTS.AI_DOCUMENT_MODAL_ABANDONED;

export const trackAIDocumentEvent = (
  posthog: PostHogInstance,
  event: AIDocumentEvent,
  properties: StandardEventProperties & {
    entry_point?: "reports_page" | "dashboard" | "report_detail";
    template_id?: string | null;
    document_id?: string;
    section_count?: number;
    /** Version ID for restore events */
    version_id?: string;
    /** Export format (pdf/docx) */
    export_format?: "pdf" | "docx";
    conversation_count?: number;
    has_active_conversation?: boolean;
    [key: string]: string | number | boolean | null | undefined;
  } = {},
) => {
  if (!posthog) return;
  try {
    posthog.capture(`ai_document_${event}`, properties);
  } catch (error) {
    console.warn("PostHog AI document tracking error:", error);
  }
};

// -----------------------------------------------------------------------------
// Chat Events
// -----------------------------------------------------------------------------

export type ChatConversationEntryPoint =
  | "auto_open"
  | "manual_open"
  | "picker_new_chat"
  | "picker_switch"
  | "existing";

type ChatEvent =
  | typeof POSTHOG_EVENTS.CHAT_CONVERSATION_CREATED
  | typeof POSTHOG_EVENTS.CHAT_CONVERSATION_SWITCHED
  | typeof POSTHOG_EVENTS.CHAT_MESSAGE_SENT
  | typeof POSTHOG_EVENTS.CHAT_NEW_CHAT_SELECTED
  | typeof POSTHOG_EVENTS.CHAT_SUGGESTION_USED;

export const trackChatEvent = (
  posthog: PostHogInstance,
  event: ChatEvent,
  properties: StandardEventProperties & {
    chat_type: "project" | "organization" | "ai_document";
    message_length?: number;
    conversation_id?: string;
    /** AI document ID (for ai_document chat type) */
    document_id?: string;
    /** User who sent the message */
    user_id?: string;
    is_first_message_in_conversation?: boolean;
    conversation_entry_point?: ChatConversationEntryPoint;
    previous_conversation_id?: string;
    selected_rank?: number;
    trigger?: "conversation_picker";
    [key: string]: string | number | boolean | undefined;
  },
) => {
  if (!posthog) return;
  try {
    posthog.capture(`chat_${event}`, properties);
  } catch (error) {
    console.warn("PostHog tracking error:", error);
  }
};

// -----------------------------------------------------------------------------
// Procurement Events
// -----------------------------------------------------------------------------

type ProcurementPackageEvent =
  | typeof POSTHOG_EVENTS.PROCUREMENT_PACKAGE_OPENED
  | typeof POSTHOG_EVENTS.PROCUREMENT_CREATION_MODAL_OPENED
  | typeof POSTHOG_EVENTS.PROCUREMENT_CREATION_FORM_COMPLETED
  | typeof POSTHOG_EVENTS.PROCUREMENT_CREATION_MODAL_CLOSED
  | typeof POSTHOG_EVENTS.PROCUREMENT_PACKAGE_EXPORTED
  | typeof POSTHOG_EVENTS.PROCUREMENT_PACKAGE_FILE_VIEWED
  | typeof POSTHOG_EVENTS.PROCUREMENT_PACKAGE_GENERATED
  | typeof POSTHOG_EVENTS.PROCUREMENT_PACKAGE_FILE_FEEDBACK
  | typeof POSTHOG_EVENTS.PROCUREMENT_PACKAGE_FILE_ADDED
  | typeof POSTHOG_EVENTS.PROCUREMENT_PACKAGE_FILE_REMOVED
  | typeof POSTHOG_EVENTS.PROCUREMENT_PACKAGE_REGENERATE_DRAWING_INDEX
  | typeof POSTHOG_EVENTS.PROCUREMENT_PACKAGE_REGENERATE_BUNDLE;

export const trackProcurementPackageEvent = (
  posthog: PostHogInstance,
  event: ProcurementPackageEvent,
  properties: StandardEventProperties & {
    package_id: string;
    project_file_id?: string;
    feedback_type?: "include" | "exclude" | "confidence" | "doublecheck" | "other";
    reason?: string;
    confidence?: number;
    /** Where the file was viewed */
    viewed_in?: "brickanta" | "sharepoint" | "onedrive";
    [key: string]: string | number | boolean | undefined;
  },
) => {
  if (!posthog) return;
  try {
    posthog.capture(`procurement_${event}`, properties);
  } catch (error) {
    console.warn("PostHog procurement tracking error:", error);
  }
};

// -----------------------------------------------------------------------------
// Settings Events
// -----------------------------------------------------------------------------

type SettingsEvent =
  | typeof POSTHOG_EVENTS.SETTINGS_PROFILE_UPDATED
  | typeof POSTHOG_EVENTS.SETTINGS_GOAL_CHANGED
  | typeof POSTHOG_EVENTS.SETTINGS_LANGUAGE_CHANGED
  | typeof POSTHOG_EVENTS.SETTINGS_INTEGRATION_CONNECTED
  | typeof POSTHOG_EVENTS.SETTINGS_INTEGRATION_DISCONNECTED
  | typeof POSTHOG_EVENTS.SETTINGS_INTEGRATION_CONFIGURED;

export const trackSettingsEvent = (
  posthog: PostHogInstance,
  event: SettingsEvent,
  properties: StandardEventProperties & {
    setting_type: string;
    old_value?: string | number | boolean;
    new_value?: string | number | boolean;
    integration_type?: string;
  },
) => {
  if (!posthog) return;
  try {
    posthog.capture(`settings_${event}`, properties);
  } catch (error) {
    console.warn("PostHog tracking error:", error);
  }
};

// -----------------------------------------------------------------------------
// Generic Cross-Module Events
// -----------------------------------------------------------------------------

/** Track feedback across any module */
export const trackFeedback = (
  posthog: PostHogInstance,
  properties: StandardEventProperties & {
    /** Type of feedback */
    feedback_type: "thumbs_up" | "thumbs_down" | "comment";
    /** What received the feedback */
    target_type: "chat_message" | "procurement_file" | "ai_document_block";
    /** ID of the target item */
    target_id?: string;
    /** Whether feedback includes a comment (text not sent for privacy) */
    has_comment?: boolean;
    /** Rating if applicable */
    rating?: "positive" | "negative";
    /** Conversation context */
    conversation_id?: string;
    convo_type?: "project" | "org";
  },
) => {
  if (!posthog) return;
  try {
    posthog.capture(POSTHOG_EVENTS.FEEDBACK_SUBMITTED, properties);
  } catch (error) {
    console.warn("PostHog tracking error:", error);
  }
};

/** Track text copying across any module */
export const trackTextCopied = (
  posthog: PostHogInstance,
  properties: StandardEventProperties & {
    /** Where the text was copied from */
    source: "chat" | "ai_document" | "procurement" | "admin" | "other";
    /** Length of copied text */
    text_length?: number;
  },
) => {
  if (!posthog) return;
  try {
    posthog.capture(POSTHOG_EVENTS.TEXT_COPIED, properties);
  } catch (error) {
    console.warn("PostHog tracking error:", error);
  }
};

/** Track general feature usage */
export const trackFeatureUsage = (
  posthog: PostHogInstance,
  feature: string,
  properties?: StandardEventProperties & { [key: string]: string | number | boolean | undefined },
) => {
  if (!posthog) return;
  try {
    posthog.capture(POSTHOG_EVENTS.FEATURE_USED, {
      feature_name: feature,
      ...properties,
    });
  } catch (error) {
    console.warn("PostHog tracking error:", error);
  }
};

/** Track role changes */
export const trackRoleChange = (
  posthog: PostHogInstance,
  properties: StandardEventProperties & {
    user_id: string;
    old_role?: string;
    new_role: string;
    changed_by?: string;
  },
) => {
  if (!posthog) return;
  try {
    posthog.capture(POSTHOG_EVENTS.ROLE_CHANGED, properties);
  } catch (error) {
    console.warn("PostHog tracking error:", error);
  }
};

/** Track errors */
export const trackError = (
  posthog: PostHogInstance,
  error: Error,
  context?: StandardEventProperties & {
    component?: string;
    action?: string;
    user_id?: string;
    component_stack?: string;
    error_boundary?: boolean;
  },
) => {
  if (!posthog) return;
  try {
    posthog.capture(POSTHOG_EVENTS.ERROR_OCCURRED, {
      error_name: error.name,
      error_message: error.message,
      error_stack: error.stack?.slice(0, 1000),
      ...context,
    });
  } catch (trackingError) {
    console.warn("PostHog error tracking failed:", trackingError);
  }
};
